/**
 * Copyright 2025-2026 Nordcloud Oy or its affiliates. All Rights Reserved.
 */

import {
  ActionWithMissingDataErrorCode,
  OsType,
  SortOrder,
} from "~/generated/graphql";

export enum UpdateReportItemStatus {
  UPDATED = "updated",
  INSTALLED = "installed",
  REMOVED = "removed",
  FAILED = "failed",
  AVAILABLE = "available",
  INSTALLED_PENDING_REBOOT = "installedPendingReboot",
}

export type UpdateReportDetail = {
  id: string;
  name: string;
  resourceId: string;
  status: UpdateReportItemStatus;
  statusLabel: string;
  time?: string | null;
  patchPolicy?: string | null;
  rebootAllowed?: boolean | null;
  releaseDate?: string | null;
  category?: string | null;
  eventId?: string | null;
  eventActionId?: string | null;
  notes?: string | null;
};

export type ActionWithMissingData = {
  eventId: string;
  eventName: string;
  eventActionId: string;
  errCode: ActionWithMissingDataErrorCode;
};

export type UpdateReportSortField =
  | "name"
  | "time"
  | "patchPolicy"
  | "rebootAllowed"
  | "releaseDate"
  | "category"
  | "status";

export type UpdateReportTableState = {
  search?: string;
  sortField: UpdateReportSortField;
  sortDirection: SortOrder;
  page: number;
  limit: number;
};

export type UpdateReportSummaryItem = {
  status: UpdateReportItemStatus;
  count: number;
};

export type UpdateReportSummary = {
  totalUpdates: number;
  byStatus: UpdateReportSummaryItem[];
};

export type UpdateReportFilterSummary = {
  statusCounts: Record<UpdateReportItemStatus, number>;
  categoryCounts: Record<string, number>;
  patchPolicyCounts: Record<string, number>;
  rebootAllowedCounts: {
    yes: number;
    no: number;
  };
};

export type UpdateReportCalculationPeriod = {
  start: string;
  end: string;
};

export type UpdateReportResourceOverview = {
  id: string;
  name: string;
  os: OsType;
  calculationPeriod: UpdateReportCalculationPeriod;
};

export type UpdateReportData = {
  resource: UpdateReportResourceOverview;
  summary: UpdateReportSummary;
  updates: UpdateReportDetail[];
  filterSummary: UpdateReportFilterSummary;
};

export type UpdateReportOverviewData = Pick<
  UpdateReportData,
  "resource" | "summary" | "filterSummary"
>;
